import React, { useEffect, useMemo, useState } from 'react';
import {useTranslation} from '../../tools/i18n';
import {makeStyles} from '@material-ui/styles';
import theme from "../theme";
import Link from "next/link";
import { AppBar, Button } from "@material-ui/core";
import {useRouter} from "next/router";
import {ChevronLeft} from "@material-ui/icons";
import SearchIcon from "@public/images/icons/search-thin.svg"

// Elements
import Topline from "./topline";
import Logo from "./logo";
import CheckoutUsps from "./checkoutUsps";
import Favorites from "./favorites";
import Cart from "./cart";
import useApplicationStore from '@services/stores/ApplicationStore';
import MobileMenu from '@components/header/menu/mobile';
import DesktopMenu from '@components/header/menu/desktop';
import Account from '@components/header/account';
import SearchResultsDropdown from '@components/header/search/parts/SearchResultsDropdown';
import SearchInput from '@components/header/search/parts/SearchInput';

const useStyles = makeStyles(() => {
    return {
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'transparent',
            color: 'inherit',
            boxShadow: 'none',
            position: 'sticky',
            top: '0',
            '@media (any-pointer: fine)': {
                zIndex: 101,
            },
            '&:hover': {
                '& .hidden': {
                    transform: 'translateY(0px)',
                }
            }
        },
        upperInner: {
            width: '100%',
            background: theme.palette.header.background,
            zIndex: 400,
        },
        lowerInner: {
            borderBottom: '1px solid #eee',
            background: theme.palette.header.background,
            width: '100%',
            transition: '0.22s cubic-bezier(.9,-0.01,.13,.99)',
            transform: 'translateY(0)',
            zIndex: 300,
            '&.hidden': {
                transform: 'translateY(calc(var(--inner-header-height) * (-1) + 1px))',
            },
            [theme.breakpoints.down('sm')]: {
                padding: '1px 10px 10px',
            },
            '&.mobile': {
                display: 'block',
                [theme.breakpoints.up('md')]: {
                    display: 'none',
                }
            },
            '&.desktop': {
                display: 'block',
                [theme.breakpoints.down('sm')]: {
                    display: 'none',
                }
            }
        },
        inner: {
            margin: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(8, minmax(auto, 44px)) repeat(2, minmax(auto, 100%)) repeat(8, minmax(auto, 44px))',
            gridAutoFlow: 'dense',
            gap: '15px 0',
            alignItems: 'center',
            maxWidth: theme.maxWidth,
            width: '100%',
            padding: '10px 15px 10px',
            zIndex: 200,
            '& svg': {
                color: theme.palette.header.icons.color,
            },
            [theme.breakpoints.up('sm')]: {
                padding: '10px 10px 10px',
            },
            [theme.breakpoints.up('lg')]: {
                padding: '10px 0 10px',
            },

        },
        innerItem1: {
            gridColumn: '1 / 2',
            display: 'flex',
            '&.mobile-menu': {
                [theme.breakpoints.up('md')]: {
                   display: 'none'
                },
            }
        },
        innerItem2: {
            gridColumn: '2 / 3',
            display: 'flex',
            '& > div + div': {
                display: 'none',
            },
            [theme.breakpoints.up('md')]: {
                gridColumn: '1 / 9',
                '& > div + div': {
                    display: 'flex',
                }
            },
        },
        innerItem3: {
            gridColumn: '9 / 13',
            display: 'flex',

            [theme.breakpoints.up('sm')]: {
                gridColumn: '9 / 11',
            },
            [theme.breakpoints.up('sm')]: {
                gridColumn: '9 / 11',
            },
        },
        innerItem4: {
            display: 'flex',
            gridColumn: '3 / 4',
            minWidth: 35,
            [theme.breakpoints.up('md')]: {
                gridColumn: '15 / 16',
            },
        },
        innerItem5: {
            display: 'flex',
            justifyContent: 'flex-end',
            paddingTop: 6,
            minWidth: 35,
            gridColumn: '16 / 17',
            [theme.breakpoints.up('sm')]: {
                paddingTop: 2,
            },
            '&.account-icon':{
                display: 'none',
                [theme.breakpoints.up('md')]: {
                    display: 'flex',
                },
            },
        },
        innerItem6: {
            gridColumn: '17 / 18',
            display: 'flex',
            justifyContent: 'flex-end',
        },
        innerItem7: {
            gridColumn: '18 / 19',
            display: 'flex',
            justifyContent: 'flex-end',
        },
        innerItem8: {

        },
        innerItem9: {
            gridColumn: '1 / 19',
            gridRow: '2 / 3',
            display: 'flex',
            flexDirection: 'column'
        },
        searchIcon: {
            opacity: 0,
            height: 20,
            width: 20,
            margin: '0 auto',
            pointerEvents: 'none',
            [theme.breakpoints.down('sm')]: {
                '.hidden &': {
                    opacity: 1,
                    pointerEvents: 'auto',
                },
            },
            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        backToStoreLink: {
            color: 'inherit',
            fontSize: 12,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            '& .label': {
                display: 'none'
            },
            '& svg': {
                fill: theme.palette.header.icons.color,
            },
            [theme.breakpoints.up('md')]: {
                '& .label': {
                    display: 'block',
                    fontSize: 12,
                    textDecoration: 'none',
                    color: theme.palette.header.color,
                    width: 'max-content',
                },
                '& svg': {
                    display: 'none'
                }
            }
        },
        checkoutTitle: {
            marginLeft: 10,
            textTransform: 'uppercase',
            fontSize: 14,
            letterSpacing: 1
        },
        obscure: {
            content: '""',
            position: 'fixed',
            zIndex: 99,
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            pointerEvents: 'none',
            display: 'none',
            opacity: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            transition: 'opacity .2s',
            transitionTimingFunction: 'ease-in',
            '&.focus': {
                opacity: 1,
                display: 'block',
                pointerEvents: 'auto',
            }
        }
    }
}, {name: 'header'});

export default function Header({menu, footer}) {
    const classes = useStyles();
    const router = useRouter();
    const {t} = useTranslation(['general']);
    const { searchOpen, menuDrawerOpen, desktopMenuOpen } = useApplicationStore(state => state.modals);
    const { toggleDrawer } = useApplicationStore(state => state.methods);
    const [isCompact, setCompact] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [scrollDirection, setScrollDirection] = useState(null);
    const [show, setShow] = useState(true);

    useEffect(() => {
        if(router.asPath === '/checkout' || router.asPath.indexOf('/checkout/order-payment') >= 0) {
            setCompact(true);
        } else {
            setCompact(false);
        }
    }, [router.asPath]);


    useEffect(() => {
        window.addEventListener("scroll", updateScrollDirection); // add event listener

        return () => {
            window.removeEventListener("scroll", updateScrollDirection); // clean up
        };
    }, [lastScrollY, scrollDirection]);

    useEffect(() => {
        if (scrollDirection === 'down' && !(menuDrawerOpen || searchOpen)) {
            setShow(false);
        } else {
            setShow(true);
        }

        if (menuDrawerOpen || searchOpen) {
            setShow(true);
        }

        document.dispatchEvent(new CustomEvent("header-scroll"));
    }, [scrollDirection, menuDrawerOpen, searchOpen]);


    function updateScrollDirection () {
        const scrollY = window.pageYOffset;
        const direction = scrollY > lastScrollY ? "down" : "up";
        const threshold = 70;
        let scrollPos = false;

        if(direction !== scrollDirection) {
            if (direction === "down" && scrollY > (lastScrollY + threshold)) {
                setScrollDirection(direction);
                scrollPos = scrollY;
            } else if (direction === "up" && scrollY < (lastScrollY - (threshold * 3))) {
                setScrollDirection(direction);
                scrollPos = scrollY;
            }
        } else {
            scrollPos = scrollY;
        }

        if (scrollY < threshold) {
            setShow(true);
            setScrollDirection("up");
        }

        if(scrollPos) {
            setLastScrollY(scrollPos);
        }
    }


    const renderData = useMemo(() => {
        return <>
            <div className={[classes.inner, isCompact ? 'compact' : '', 'header-bounding-box'].join(' ')}>

                {isCompact && (
                    <Link href={'/'} className={classes.innerItem1}>
                        <a className={classes.backToStoreLink}>
                            <Button>
                                <ChevronLeft />
                                <span className={'label'}>{t('back-to-store')}</span>
                            </Button>
                        </a>
                    </Link>
                )}

                {!isCompact && (
                    <>
                        <div className={[classes.innerItem1, 'mobile-menu'].join(' ')}>
                            <MobileMenu menu={menu} footer={footer}/>
                        </div>

                        <div className={classes.innerItem2}>
                            <div className={classes.searchIcon} onClick={()=> {toggleDrawer('searchOpen');}}><SearchIcon/></div>
                            <SearchInput />
                        </div>
                    </>
                )}

                <div className={classes.innerItem3}>
                    <Logo />
                </div>

                {!isCompact && (
                    <>
                        <div className={[classes.innerItem5, 'account-icon'].join(' ')}>
                            <Account />
                        </div>

                        <div className={classes.innerItem6}>
                            <Favorites />
                        </div>

                        <div className={classes.innerItem7}>
                            <Cart />
                        </div>

                    </>
                )}
            </div>

            {isCompact && <CheckoutUsps />}
        </>
    }, [isCompact])


    const renderDesktop = useMemo(() => {
        return <DesktopMenu menu={menu}/>
    }, [])


    return (
        <>
            {!isCompact && <Topline className={!show ? 'hidden' : ''} />}
            <AppBar className={classes.wrapper}>
                <>
                    <div className={[classes.upperInner, !show ? 'hidden' : ''].join(' ')}>
                        {renderData}
                    </div>
                    <div className={[classes.lowerInner, 'header-lower-inner', 'mobile', !show ? 'hidden' : ''].join(' ')}>
                        { !isCompact && <SearchInput /> }
                    </div>
                    <div className={[classes.lowerInner, 'header-lower-inner', 'desktop', !show ? 'hidden' : ''].join(' ')}>
                        { !isCompact && renderDesktop }
                    </div>
                    <SearchResultsDropdown />
                </>
            </AppBar>

            <div className={[classes.obscure, searchOpen ? 'focus' : ''].join(' ')} onClick={toggleDrawer}></div>
        </>
    );
}
